import React, { useEffect, useState } from 'react';
import { Box, Modal, Typography, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { DataGrid } from '@mui/x-data-grid';
import { getDCStockTransferItemList_001 } from '../../Request/actions';

const num0 = (v) => Number(v || 0).toFixed(0);
const num2 = (v) => Number(v || 0).toFixed(2);

export default function DCTransferItemsModal({ open, onClose, headObj }) {
  const [sItems, setItems] = useState([]);
  const [sLoading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!open || !headObj?.T_VocNo) return;
      setLoading(true);
      const obj = {
        vocType: headObj.T_VocTyp,
        vocNo: headObj.T_VocNo,
        loc: headObj.T_Loc
      };
      const result = await getDCStockTransferItemList_001(obj);
      if (!active) return;
      const rows = (result?.ResultSet || []).map((i, idx) => ({ ...i, id: idx + 1 }));
      setItems(rows);
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [open, headObj?.T_VocTyp, headObj?.T_VocNo, headObj?.T_Loc]);

  const columns = [
    { field: 'IL_ItmCd', headerName: 'Item Code', width: 110 },
    { field: 'ItemName', headerName: 'Item Name', width: 200 },
    {
      field: 'B_Tsr_PackQty',
      headerName: 'Pack Qty',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: ({ value }) => num0(value)
    },
    {
      field: 'IL_StockCTN',
      headerName: 'Stock',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: ({ value }) => num0(value)
    },
    {
      field: 'DCPickShowing',
      headerName: 'DCPick',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const v = Number(params.value);
        return <span>{!v ? '' : v.toFixed(2)}</span>;
      }
    },
    {
      field: 'B_Tsr_Qty',
      headerName: 'Qty',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: ({ value }) => num0(value)
    },
    {
      field: 'B_Tsr_BaseQty',
      headerName: 'Base Qty',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: ({ value }) => num0(value)
    },
    { field: 'B_Tsr_Unit', headerName: 'Unit', width: 70, align: 'center', headerAlign: 'center' },
    {
      field: 'B_Tsr_UnitRate',
      headerName: 'Unit Rate',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => num2(value)
    },
    {
      field: 'IL_OverHead',
      headerName: 'OverHead',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => num2(value)
    },
    { field: 'IL_Type', headerName: 'Type', width: 80, align: 'center', headerAlign: 'center' }
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '96vw', sm: '92vw', md: '82vw' },
          maxWidth: 1100,
          maxHeight: '92vh',
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none'
        }}>
        {/* header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
            color: '#fff',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0
          }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
            DC Stock Transfer — {headObj?.T_VocNo}
          </Typography>
          <Chip
            label={(headObj?.T_DocStat || '').toString().toUpperCase()}
            size="small"
            sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#fff', color: '#7c3aed' }}
          />
          <CloseIcon onClick={onClose} sx={{ cursor: 'pointer', fontSize: 20, ml: 0.5 }} />
        </Box>

        {/* info strip */}
        <Box
          sx={{
            px: 2,
            py: 0.75,
            bgcolor: '#f5f3ff',
            borderBottom: '1px solid #e6ebf2',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            flexShrink: 0
          }}>
          {[
            ['Voucher', headObj?.vocName || headObj?.T_VocTyp],
            ['Branch', headObj?.LocName],
            ['DC Loc', headObj?.DCLocName],
            ['Department', headObj?.Department]
          ].map(([l, v]) => (
            <Box key={l} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
                {l}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a2a3a' }}>
                {v || '—'}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* grid */}
        <Box sx={{ flex: 1, p: 1.25, overflow: 'hidden' }}>
          {sLoading ? (
            <Box sx={{ height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: '55vh', width: '100%' }}>
              <DataGrid
                getRowId={(row) => row.id}
                rows={sItems}
                columns={columns}
                rowHeight={32}
                headerHeight={34}
                hideFooterPagination
                hideFooter={sItems.length > 0 ? false : true}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'mui-even-row' : 'mui-odd-row'
                }
                sx={{
                  border: '1px solid #e6ebf2',
                  borderRadius: 1.5,
                  '.MuiDataGrid-columnHeaders': {
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%) !important',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700
                  },
                  '.MuiDataGrid-cell': { fontSize: 12, color: '#0f172a' }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
